import { isDemoMode, supabase } from '@/lib/supabase';
import { buildProjectChatHref, projectDecisionHref } from '@/lib/navigation/projectChat';
import { SHOWCASE_WORKSPACE_ID } from '@/demo/showcaseCatalog';
import { getCallSession } from '@/features/calls/callService';
import type { Decision } from '@/types/decisions';

/** Resolve actual source scope under RLS; never guess a room from a project id. */
export async function decisionSourceHref(decision: Decision): Promise<string> {
  const returnTo = decision.project_id ? projectDecisionHref(decision.project_id, decision.id, isDemoMode ? SHOWCASE_WORKSPACE_ID : decision.workspace_id) : undefined;
  const sourceId = decision.source_message_id || decision.source_thread_id;
  if (sourceId) {
    let roomId: string | undefined;
    let threadId: string | undefined;
    if (isDemoMode) {
      let stored: Record<string, Array<{ id: string; threadParentId?: string }>> = {};
      try { stored = JSON.parse(localStorage.getItem('AX1_DEMO_MESSAGES') || '{}'); } catch { /* Unavailable demo source. */ }
      for (const [id, messages] of Object.entries(stored)) {
        const source = Array.isArray(messages) ? messages.find(message => message.id === sourceId) : undefined;
        if (source) { roomId = id; threadId = source.threadParentId || source.id; break; }
      }
    } else {
      const { data, error } = await supabase.from('chat_messages').select('id,room_id,thread_parent_id')
        .eq('id', sourceId).is('deleted_at', null).maybeSingle();
      if (error) throw new Error('The source could not be loaded. Please try again.');
      if (data) { roomId = data.room_id; threadId = data.thread_parent_id || data.id; }
    }
    if (!roomId || !threadId) throw new Error('The original conversation is unavailable or you no longer have access. The decision record is still preserved.');
    return buildProjectChatHref({ roomId, threadId, returnTo, workspaceId: isDemoMode ? SHOWCASE_WORKSPACE_ID : decision.workspace_id });
  }
  if (decision.source_call_session_id) {
    const result = await getCallSession(decision.source_call_session_id);
    if (!result.data?.room_id || result.error) throw new Error('The original call is unavailable or you no longer have access.');
    return buildProjectChatHref({ roomId: result.data.room_id, returnTo, workspaceId: isDemoMode ? SHOWCASE_WORKSPACE_ID : decision.workspace_id }) + `&call=${encodeURIComponent(decision.source_call_session_id)}`;
  }
  throw new Error('This decision does not have a linked conversation.');
}
